import React, { useState } from 'react'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useStatusBanners } from 'redux/hooks/StatusBanner'

import Box from 'components_new/atoms/Box'
import StatusBanner from 'components_new/molecules/StatusBanner'
import {
  StatusBannerItem,
  sortByLevel
} from 'components_new/molecules/StatusBanner/lib'

/*-- local storage --*/
const LS_HIDDEN_BANNERS_KEY = 'hidden_banners'

interface LSHiddenBanner {
  id: string
  timestamp: number
}
/*-- --*/

const EXPIRE_IN_HOURS = 4

export const appendEmailToLink = (
  link: string | null,
  appendEmail: boolean | undefined,
  email: string | undefined
): string | null => {
  if (!link || !appendEmail || !email) {
    return link
  }

  const separator = link.includes('?') ? '&' : '?'

  return `${link}${separator}email=${encodeURIComponent(email)}`
}

function getHiddenBanners() {
  const storedHiddenBanners = localStorage.getItem(LS_HIDDEN_BANNERS_KEY)

  if (storedHiddenBanners) {
    const parsed = JSON.parse(storedHiddenBanners)
    const now = Date.now()

    // filter out expired entries
    const validBanners: LSHiddenBanner[] = parsed.filter(
      (banner: LSHiddenBanner) => banner.timestamp > now
    )

    localStorage.setItem(LS_HIDDEN_BANNERS_KEY, JSON.stringify(validBanners))

    return validBanners.map((banner) => banner.id)
  }

  return []
}

const BannerBar = () => {
  const banners = useStatusBanners()
  const { account } = useAccountSettings()

  const [hiddenBanners, setHiddenBanners] = useState<string[]>(
    getHiddenBanners()
  )
  const [expanded, setExpanded] = useState(false)

  const handleClose = (id: string) => {
    const now = Date.now()
    const expiration = now + EXPIRE_IN_HOURS * 60 * 60 * 1000 // hours in ms

    setHiddenBanners([...hiddenBanners, id])

    const updatedStoredHiddenBanners = [
      ...(JSON.parse(localStorage.getItem(LS_HIDDEN_BANNERS_KEY) || '[]') as {
        id: string
        timestamp: number
      }[]),
      { id, timestamp: expiration }
    ]

    localStorage.setItem(
      LS_HIDDEN_BANNERS_KEY,
      JSON.stringify(updatedStoredHiddenBanners)
    )
  }

  const visibleBanners = sortByLevel(
    banners
      .filter((banner) => !hiddenBanners.includes(banner.id))
      .map(
        (banner): StatusBannerItem => ({
          id: banner.id,
          level: banner.level === 'important' ? 'important' : 'info',
          title: banner.title,
          description: banner.description,
          href: appendEmailToLink(
            banner.link,
            banner.append_email_to_link,
            account?.email
          ),
          linkLabel: banner.link_label
        })
      )
  )

  if (visibleBanners.length === 0) return null

  return (
    <Box
      sx={{
        position: 'relative',
        // Between appBar (1100) and drawer (1200): the expanded list
        // must overlay the sticky Topbar but stay below drawers/modals.
        zIndex: 1150
      }}
    >
      <StatusBanner
        banners={visibleBanners}
        expanded={expanded && visibleBanners.length > 1}
        onClose={handleClose}
        onToggleExpanded={() => setExpanded(!expanded)}
      />
    </Box>
  )
}

export default BannerBar
